import { useEffect, useState } from 'react'
import Button from './Button'
import FormRow from './FormRow'
import Modal from './Modal'
import TextField from './TextField'
import { kit } from './strings'

/**
 * One question, one field, one answer — the legacy's small centred modals with a single
 * input in them (`mdlTestEmailSending` is the shape this was drawn from).
 *
 * It is here rather than in a feature folder because the two screens that ask this kind of
 * question — the mail settings test and the phishing sender test — ask it identically, and a
 * dialog duplicated per feature is a dialog that drifts per feature.
 *
 * The value lives here: the caller gets it on submit and never has to hold it while the
 * dialog is closed. Enter submits, an empty answer cannot.
 *
 * @param {string} label  what the field is asking for
 * @param {string} [hint]  one line under the field
 * @param {boolean} [busy]  the answer is being acted on — the confirm button waits
 * @param {(value: string) => void} onSubmit
 */
export default function PromptDialog({
  open,
  title,
  subtitle,
  icon,
  label,
  hint,
  placeholder,
  type = 'text',
  dir,
  confirmLabel,
  cancelLabel = kit.close,
  busy = false,
  onSubmit,
  onClose,
}) {
  const [value, setValue] = useState('')

  // A fresh dialog never opens onto the last answer.
  useEffect(() => {
    if (open) setValue('')
  }, [open])

  const submit = (event) => {
    event.preventDefault()
    if (!value.trim() || busy) return
    onSubmit?.(value.trim())
  }

  return (
    <Modal
      open={open}
      title={title}
      subtitle={subtitle}
      icon={icon}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="prompt-dialog-form" disabled={busy || !value.trim()}>
            {confirmLabel}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {cancelLabel}
          </Button>
        </>
      }
    >
      <form id="prompt-dialog-form" onSubmit={submit}>
        <FormRow label={label} hint={hint}>
          {(id) => (
            <TextField
              id={id}
              type={type}
              dir={dir}
              value={value}
              onChange={setValue}
              placeholder={placeholder}
              autoComplete="off"
            />
          )}
        </FormRow>
      </form>
    </Modal>
  )
}
